import { Card, CardContent, Grid, Typography, Box, Divider, IconButton, Tooltip } from '@mui/material'
import { EmployerStatusDropDown } from './EmployerStatusDropDown'
import {
  ClosingDateTypeEnum,
  CompanyStatusEnum,
  PaymentPartnerEnum,
  getCompanyCreateSchema,
  ICompany,
  useCompanyStatusMutation,
  useCompanyUpdateMutation,
  useUpdateAvatarCompanyMutation
} from '@/@data'
import { useNotification } from '@/hooks/useNotification'
import { getInitials } from '@/utils/getInitials'
import CustomAvatar from '@/@core/components/mui/Avatar'
import { useTranslator } from '@/hooks/useTranslator'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { Button } from '@/@core/components/mui/Button'
import React, { useState, useEffect, useRef } from 'react'
import { FormInput } from '../form/FormInput'
import BankSelection from '../selection/BankSelection'
import InputDateField from '../common/input/InputDateField'
import PhoneCodeSelection from '../selection/PhoneCodeSelection'
import dayjs from 'dayjs'
import FormSelect from '../form/FormSelect'
import { yupResolver } from '@hookform/resolvers/yup'

export const EmployerSetting = ({
  onClose,
  employer,
  refetch
}: {
  onClose: () => void
  employer: ICompany
  refetch: () => void
}) => {
  const n = useNotification()
  const { __ } = useTranslator()

  const { mutate: updateCompany } = useCompanyUpdateMutation()
  const [isExpand, setExpand] = useState(true)
  const updateAvatarCompanyMutation = useUpdateAvatarCompanyMutation()

  const closingDateTypeOptions = [
    { value: 'before', label: __('Previous Month') },
    { value: 'after', label: __('Next Month') }
  ]
  const paymentPartnerOptions = [
    { value: PaymentPartnerEnum.VnptEpay, label: 'VNPT ePay' },
    { value: PaymentPartnerEnum.Shinhan, label: 'Shinhan Bank' }
  ]

  const closingDateOptions = [
    { value: 'end_month', label: __('End of Month') },
    { value: 'first_month', label: __('First of Month') },
    { value: 'enter_date', label: __('Enter Date') }
  ]
  const withdrawCutoffOptions = [
    { value: 'end_month', label: __('End of Month') },
    { value: 'enter_date', label: __('Enter Date') }
  ]

  useEffect(() => {
    methods.reset({
      logo: employer.logo_file,
      name: employer.name,
      short_name: employer.short_name,
      company_type: employer.company_type,
      email: employer.email,
      bank_id: employer.bank_id,
      address: employer.address,
      establish_date: employer.establish_date ? dayjs(employer.establish_date).format('YYYY-MM-DD') : '',
      poc_phone_code: employer.poc_phone_code,
      poc_firstname: employer.poc_firstname,
      poc_phone: employer.poc_phone,
      business_number: employer.business_number,
      bank_account_number: employer.bank_account_number,
      closing_date_type: employer.closing_date_type,
      closing_specific_date:
        employer.closing_date === 0 ? 'end_month' : employer.closing_date === 1 ? 'first_month' : 'enter_date',
      closing_date: employer.closing_date,
      minimum_working_days: employer.minimum_working_days,
      withdraw_cutoff_option: Number(employer.withdraw_cutoff_day || 0) === 0 ? 'end_month' : 'enter_date',
      withdraw_cutoff_day: Number(employer.withdraw_cutoff_day || 0) === 0 ? '' : employer.withdraw_cutoff_day,
      memo: employer.memo
      ,
      payment_partner: employer.payment_partner || PaymentPartnerEnum.VnptEpay
    })
  }, [employer])

  const methods = useForm<any>({
    defaultValues: {
      logo: null,
      name: '',
      short_name: '',
      company_type: '',
      email: '',
      bank_id: null,
      address: '',
      establish_date: '',
      poc_phone_code: '',
      poc_firstname: '',
      poc_phone: '',
      business_number: '',
      bank_account_number: '',
      closing_date_type: '',
      closing_date: '',
      minimum_working_days: '',
      withdraw_cutoff_option: 'end_month',
      withdraw_cutoff_day: '',
      memo: '',
      payment_partner: PaymentPartnerEnum.VnptEpay
    },
    mode: 'onChange',
    resolver: yupResolver(getCompanyCreateSchema(__) as any)
  })

  const statusBgColor: Record<string, string> = {
    active: '#D4F4E2',
    disable: '#EAEAEC'
  }

  const hiddenFileInputRef = useRef<HTMLInputElement>(null)

  const handleChooseClick = () => {
    hiddenFileInputRef.current?.click()
  }

  const getAvatar = (params: Pick<ICompany, 'logo_file' | 'name'>) => {
    const { logo_file, name } = params

    if (logo_file) {
      return <CustomAvatar src={logo_file.tiny_url} skin='light' size={120} />
    } else {
      return (
        <CustomAvatar skin='light' size={120}>
          {getInitials(name as string)}
        </CustomAvatar>
      )
    }
  }

  const { mutate: updateCompanyStatus } = useCompanyStatusMutation()

  const handleHiddenFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if (file && employer) {
      updateAvatarCompanyMutation.mutate(
        {
          id: employer.id,
          avatar: file
        },
        {
          onSuccess: () => {
            refetch?.()
            n.success(__('Avatar updated successfully'))
          },
          onError: error => {
            n.error(__('Failed to update avatar: ') + error.message)
          }
        }
      )
    }
  }

  const onSubmit = (data: any) => {
    const closingDateType: Record<string, string> = {
      end_month: 'before',
      first_month: 'after',
      enter_date: data.closing_date_type
    }

    let payload = {
      ...data,
      closing_date: Number(data.closing_date),
      minimum_working_days: Number(data.minimum_working_days),
      withdraw_cutoff_day:
        data.withdraw_cutoff_option === 'end_month'
          ? 0
          : Number(data.withdraw_cutoff_day),
      closing_date_type: closingDateType[data.closing_specific_date]
    }

    delete payload.closing_specific_date
    delete payload.withdraw_cutoff_option

    if (data.logo instanceof FileList && data.logo.length > 0) {
      const formData = new FormData()

      formData.append('logo', data.logo[0])

      Object.keys(payload).forEach(key => {
        if (key !== 'logo' && payload[key] !== undefined && payload[key] !== null) {
          formData.append(key, String(payload[key]))
        }
      })

      payload = formData
    }

    updateCompany(
      { id: employer.id, data: payload },
      {
        onSuccess: () => {
          n.success(__('Update successfully'))
          refetch()
          onClose()
        },
        onError: error => {
          n.error(error.message)
        }
      }
    )
  }

  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (name === 'closing_specific_date') {
        const selectedOption = value.closing_specific_date

        if (selectedOption === 'end_month') {
          methods.setValue('closing_date', '0')
          methods.setValue('closing_date_type', 'before')
        } else if (selectedOption === 'first_month') {
          methods.setValue('closing_date', '1')
          methods.setValue('closing_date_type', 'after')
        } else if (selectedOption === 'enter_date') {
          methods.setValue('closing_date', '')
          methods.setValue('closing_date_type', '')
        }
      }
      if (name === 'withdraw_cutoff_option') {
        if (value.withdraw_cutoff_option === 'end_month') {
          methods.setValue('withdraw_cutoff_day', '')
        }
      }
    })

    return () => {
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe()
      }
    }
  }, [methods])

  const getRangeWorkingDay = (
    closingDate: number,
    closingDateType: ClosingDateTypeEnum,
    payPeriod: string
  ): {
    startDate: string
    endDate: string
  } => {
    const firstDayOfMonth = dayjs(payPeriod).format('YYYY-MM-01')

    if (closingDate === 0) {
      if (closingDateType === ClosingDateTypeEnum.Before) {
        return {
          startDate: firstDayOfMonth,
          endDate: dayjs(firstDayOfMonth).endOf('month').format('YYYY-MM-DD')
        }
      } else {
        const nextMonth = dayjs(firstDayOfMonth).add(1, 'month')

        return {
          startDate: nextMonth.format('YYYY-MM-DD'),
          endDate: nextMonth.endOf('month').format('YYYY-MM-DD')
        }
      }
    }

    const closingDateForMonth = dayjs(firstDayOfMonth).set('date', closingDate).format('YYYY-MM-DD')

    if (closingDateType === ClosingDateTypeEnum.After) {
      return {
        startDate: dayjs(closingDateForMonth).add(1, 'day').format('YYYY-MM-DD'),
        endDate: dayjs(closingDateForMonth).add(1, 'month').format('YYYY-MM-DD')
      }
    } else {
      return {
        startDate: dayjs(closingDateForMonth).subtract(1, 'month').add(1, 'day').format('YYYY-MM-DD'),
        endDate: closingDateForMonth
      }
    }
  }

  const closingSpecificDate = methods.watch('closing_specific_date')
  const closingDateType = methods.watch('closing_date_type')
  const closingDate = methods.watch('closing_date')

  const isSubmitDisabled = () => {
    if (!closingSpecificDate || !closingDateType) {
      return false
    }

    if (closingSpecificDate === 'enter_date' && (!closingDate || closingDate === '')) {
      return false
    }

    let dateValue = 0

    if (closingSpecificDate === 'end_month') {
      dateValue = 0
    } else if (closingSpecificDate === 'first_month') {
      dateValue = 1
    } else if (closingSpecificDate === 'enter_date') {
      dateValue = Number(closingDate) || 0
    }

    const payPeriod = employer?.current_pay_period || dayjs().format('YYYY-MM-DD')
    const range = getRangeWorkingDay(dateValue, closingDateType, payPeriod)
    const today = dayjs().format('YYYY-MM-DD')
    const startDate = range.startDate

    return dayjs(startDate).isAfter(dayjs(today))
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className='flex justify-between items-start gap-5'>
          <Card
            sx={{
              borderRadius: 3,
              flex: 1,
              backgroundColor: statusBgColor[employer.status?.toLowerCase()] || '#F9F9F9'
            }}
          >
            <CardContent>
              <Box className='flex items-star justify-between mb-2'>
                <Box sx={{ display: 'flex', gap: 3, mb: 3 }} className='items-start'>
                  <div className='border rounded-full'>
                    {getAvatar({
                      logo_file: methods.watch('logo') || employer?.logo_file,
                      name: employer?.name || '-'
                    })}
                  </div>
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Tooltip title={employer.name}>
                        <Typography
                          variant='h5'
                          gutterBottom
                          sx={{ fontWeight: 'medium', fontSize: '28px', mb: 0 }}
                          className='max-w-[400px] whitespace-nowrap overflow-hidden text-ellipsis'
                        >
                          {employer.name}
                        </Typography>
                      </Tooltip>
                      <EmployerStatusDropDown
                        status={employer.status as CompanyStatusEnum}
                        onSelect={status => {
                          updateCompanyStatus(
                            {
                              id: employer.id,
                              data: {
                                status: status as CompanyStatusEnum
                              }
                            },
                            {
                              onSuccess: () => {
                                refetch()
                                n.success(__('Status updated successfully'))
                              },
                              onError: () => {
                                n.error(__('Status update failed'))
                              }
                            }
                          )
                        }}
                        sx={{
                          height: '30px',
                          fontSize: '13px'
                        }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <i className='tabler-id text-[18px] text-[#2f2b3d66]' />
                      <span>{employer?.system_code}</span>
                      <IconButton
                        size='small'
                        sx={{
                          width: 40,
                          height: 40,
                          '&:hover': {
                            backgroundColor: '#e0e0e0'
                          }
                        }}
                        onClick={() => {
                          navigator.clipboard.writeText(employer.system_code || '')
                          n.success(__('Copied to clipboard!'))
                        }}
                      >
                        <i className='tabler-copy text-[#2f2b3db3]' />
                      </IconButton>
                    </Box>
                    <Box className='flex items-center flex-row gap-2'>
                      <Button
                        variant='outlined'
                        color='primary'
                        type='button'
                        className='bg-[#fff] h-[30px]'
                        onClick={handleChooseClick}
                      >
                        <span className='text-[13px] font-medium'>{__('Upload Logo')}</span>
                      </Button>
                      <Typography variant='body2' className='text-[#2F2B3D66] text-[13px] font-normal'>
                        {__('At least 800x800 px recommended JPG or PNG is allowed')}
                      </Typography>
                      <input
                        ref={hiddenFileInputRef}
                        type='file'
                        accept='image/*'
                        style={{ display: 'none' }}
                        onChange={handleHiddenFileChange}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <IconButton size='large' className='bg-[#ffffff] w-[38px] h-[38px]' onClick={onClose}>
                    <i className='tabler-x w-[20px] h-[20px] text-black' />
                  </IconButton>
                  <IconButton
                    size='large'
                    className='bg-[#ffffff] w-[38px] h-[38px]'
                    onClick={() => {
                      setExpand(!isExpand)
                    }}
                  >
                    <i className='tabler-viewport-wide w-[20px] h-[20px] text-black' />
                  </IconButton>
                </Box>
              </Box>

              {/* Employer form Info */}
              <Card className='shadow-none border rounded-[16px] bg-[#fff] px-5 py-6'>
                <Typography className='text-[18px] font-medium text-[#2f2b3de6]'>{__('Employer Info')}</Typography>

                <Divider sx={{ my: 4 }} />

                <Grid container spacing={5} className='overflow-y-auto'>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
                      {__('Employer Full Name')} <span className='text-[#FF4C51]'>*</span>
                    </div>
                    <FormInput
                      name='name'
                      placeholder={__('Enter Employer')}
                      control={methods.control}
                      onClear={() => methods.setValue('name', '')}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
                      {__('Employer Short Name')} <span className='text-[#FF4C51]'>*</span>
                    </div>
                    <FormInput
                      name='short_name'
                      placeholder={__('Enter Employer Short Name')}
                      control={methods.control}
                      onClear={() => methods.setValue('short_name', '')}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
                      {__('Employer Registration')} <span className='text-[#FF4C51]'>*</span>
                    </div>
                    <FormInput
                      name='business_number'
                      placeholder={__('Enter Employer Registration')}
                      control={methods.control}
                      onClear={() => methods.setValue('business_number', '')}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormInput
                      label={__('POC Name')}
                      name='poc_firstname'
                      placeholder={__('Enter POC Name')}
                      control={methods.control}
                      onClear={() => methods.setValue('poc_firstname', '')}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormInput
                      label={__('POC Email')}
                      name='email'
                      placeholder={__('Enter POC Email')}
                      control={methods.control}
                      onClear={() => methods.setValue('email', '')}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography className='text-[15px] font-medium leading-[15px] mb-1'>{__('POC Phone')}</Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 4 }}>
                        <Controller
                          name='poc_phone_code'
                          control={methods.control}
                          render={({ field }) => <PhoneCodeSelection {...field} placeholder={__('Country')} />}
                        />
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <FormInput
                          name='poc_phone'
                          placeholder={__('Enter Phone')}
                          control={methods.control}
                          fullWidth
                          onClear={() => methods.setValue('poc_phone', '')}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name='establish_date'
                      control={methods.control}
                      render={({ field }) => (
                        <InputDateField
                          {...field}
                          name='establish_date'
                          value={methods.getValues('establish_date')}
                          placeholder={__('Select Date')}
                          label={__('Establish Date')}
                          onChange={value => methods.setValue('establish_date', value)}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormInput
                      label={__('Employer Type')}
                      name='company_type'
                      placeholder={__('Enter Employer Type')}
                      control={methods.control}
                      onClear={() => methods.setValue('company_type', '')}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
                      {__('Employer Bank Name')} <span className='text-[#FF4C51]'>*</span>
                    </div>
                    <Controller
                      name='bank_id'
                      control={methods.control}
                      render={({ field }) => (
                        <BankSelection
                          {...field}
                          fullWidth
                          placeholder={__('Select Bank')}
                          onClear={() => methods.setValue('bank_id', '')}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
                      {__('Employer Bank Account')} <span className='text-[#FF4C51]'>*</span>
                    </div>
                    <FormInput
                      name='bank_account_number'
                      placeholder={__('Enter Bank Account')}
                      control={methods.control}
                      onClear={() => methods.setValue('bank_account_number', '')}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
                      {__('Payment Partner')} <span className='text-[#FF4C51]'>*</span>
                    </div>
                    <FormSelect
                      name='payment_partner'
                      options={paymentPartnerOptions}
                      placeholder={__('Select Payment Partner')}
                      height='36.6px'
                      onClear={() => methods.setValue('payment_partner', PaymentPartnerEnum.VnptEpay)}
                      className='bg-[#fff]'
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <FormInput
                      label={__('Employer Address')}
                      name='address'
                      placeholder={__('Enter Address')}
                      control={methods.control}
                      onClear={() => methods.setValue('address', '')}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
                      {__('Timesheet Closing Date')} <span className='text-[#FF4C51]'>*</span>
                    </div>
                    <div
                      className={`grid ${methods.watch('closing_specific_date') !== 'enter_date' ? 'grid-cols-1' : 'grid-cols-2'}  gap-4`}
                    >
                      <div>
                        <div
                          className={`grid ${methods.watch('closing_specific_date') === 'enter_date' ? 'grid-cols-2' : 'grid-cols-1'} gap-1`}
                        >
                          <div>
                            <FormSelect
                              name='closing_specific_date'
                              placeholder={__('Choose Options')}
                              options={closingDateOptions}
                              height='36.6px'
                              onClear={() => {
                                methods.setValue('closing_date', '')
                                methods.setValue('closing_specific_date', '')
                              }}
                              onChange={() => {
                                methods.clearErrors()
                              }}
                              className='bg-[#fff]'
                            />
                          </div>
                          <div
                            className={`${methods.watch('closing_specific_date') === 'enter_date' ? 'block' : 'hidden'}`}
                          >
                            <Controller
                              name='closing_date'
                              control={methods.control}
                              render={({ field }) => {
                                const closingDateOption = methods.watch('closing_specific_date')
                                const isDisabled = closingDateOption !== 'enter_date'

                                return (
                                  <FormInput
                                    {...field}
                                    name='closing_date'
                                    placeholder={__('Enter Date')}
                                    control={methods.control}
                                    disabled={isDisabled}
                                    type='number'
                                    slotProps={{
                                      input: {
                                        endAdornment: (
                                          <span className='text-[#2F2B3D66] text-[14px] ml-2'>{__('Day')}</span>
                                        )
                                      }
                                    }}
                                  />
                                )
                              }}
                            />
                          </div>
                        </div>
                        {(() => {
                          const closingSpecificDate = methods.watch('closing_specific_date')
                          const closingDateType = methods.watch('closing_date_type')
                          const closingDate = methods.watch('closing_date')

                          if (!closingSpecificDate || !closingDateType) {
                            return false
                          }

                          if (closingSpecificDate === 'enter_date' && (!closingDate || closingDate === '')) {
                            return false
                          }

                          return true
                        })() && (
                          <div className='text-[13px] font-normal text-[#2f2b3d8c] mt-1'>
                            {(() => {
                              const closingSpecificDate = methods.watch('closing_specific_date')
                              const closingDateType = methods.watch('closing_date_type')
                              const closingDate = methods.watch('closing_date')

                              let dateValue = 0

                              if (closingSpecificDate === 'end_month') {
                                dateValue = 0 // End of month
                              } else if (closingSpecificDate === 'first_month') {
                                dateValue = 1 // First of month
                              } else if (closingSpecificDate === 'enter_date') {
                                dateValue = Number(closingDate) || 0
                              }

                              const payPeriod = employer?.current_pay_period || dayjs().format('YYYY-MM-DD')
                              const range = getRangeWorkingDay(dateValue, closingDateType, payPeriod)
                              const payPeriodDate = dayjs(payPeriod)

                              return `${__('VD: Kỳ lương Tháng')} ${payPeriodDate.month() + 1}/${payPeriodDate.year()} : ${range.startDate} → ${range.endDate}`
                            })()}
                          </div>
                        )}
                      </div>
                      {/* <pre>
                        {JSON.stringify(employer?.current_pay_period, null, 2)}
                      </pre> */}

                      <div
                        className={`${methods.watch('closing_specific_date') !== 'enter_date' ? 'hidden' : 'block'}`}
                      >
                        <div>
                          <FormSelect
                            name='closing_date_type'
                            placeholder={__('Choose Closing Date Type')}
                            options={closingDateTypeOptions}
                            height='36.6px'
                            onClear={() => methods.setValue('closing_date_type', '')}
                            className='bg-[#fff]'
                          />
                        </div>
                      </div>
                    </div>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
                      {__('Minimum Working Days')} <span className='text-[#FF4C51]'>*</span>
                    </div>
                    <FormInput
                      name='minimum_working_days'
                      placeholder={__('Enter Minimum Working Days')}
                      control={methods.control}
                      onClear={() => methods.setValue('minimum_working_days', '')}
                      fullWidth
                      type='number'
                      slotProps={{
                        input: {
                          inputProps: {
                            min: 0,
                            max: 30,
                            step: 1
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
                      {__('Memo')} <span className='text-[#FF4C51]'>*</span>
                    </div>
                    <FormInput
                      name='memo'
                      placeholder={__('Enter Memo')}
                      control={methods.control}
                      onClear={() => methods.setValue('memo', '')}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
                      {__('Withdraw Cutoff Day')} <span className='text-[#FF4C51]'>*</span>
                    </div>
                    <div
                      className={`grid ${methods.watch('withdraw_cutoff_option') === 'enter_date' ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}
                    >
                      <FormSelect
                        name='withdraw_cutoff_option'
                        placeholder={__('Choose Options')}
                        options={withdrawCutoffOptions}
                        height='36.6px'
                        onClear={() => {
                          methods.setValue('withdraw_cutoff_option', 'end_month')
                          methods.setValue('withdraw_cutoff_day', '')
                        }}
                        className='bg-[#fff]'
                      />
                      <div className={`${methods.watch('withdraw_cutoff_option') === 'enter_date' ? 'block' : 'hidden'}`}>
                        <FormInput
                          name='withdraw_cutoff_day'
                          placeholder={__('Enter Date')}
                          control={methods.control}
                          fullWidth
                          type='number'
                          slotProps={{
                            input: {
                              inputProps: {
                                min: 1,
                                max: 30,
                                step: 1
                              },
                              endAdornment: <span className='text-[#2F2B3D66] text-[14px] ml-2'>{__('Day')}</span>
                            }
                          }}
                        />
                      </div>
                    </div>
                  </Grid>
                </Grid>
              </Card>
            </CardContent>
          </Card>
          {isExpand && (
            <div>
              <Card sx={{ borderRadius: 3, width: '266px' }} className='p-5'>
                <Button
                  variant='contained'
                  className='text-[#6F83F6] bg-[#E8EBFD] shadow-none mb-3'
                  fullWidth
                  onClick={() => methods.reset()}
                >
                  {__('Reset')}
                </Button>
                <Button
                  variant='contained'
                  className='text-[#28C76F] bg-[#DCF6E8] shadow-none'
                  fullWidth
                  type='submit'
                  disabled={isSubmitDisabled()}
                >
                  {__('Save')}
                </Button>
              </Card>
            </div>
          )}
        </div>
      </form>
    </FormProvider>
  )
}
